import type { SensorAggregate, MomentMonth, MonthDailyMeans } from "@weather-deck/shared";
import { el } from "./dom";

export interface MonthRankRow {
  year: number;
  mean: number;
  days: number;
  rank: number;
  isCurrent: boolean;
}

export interface MonthRanking {
  month: number;
  monthLabel: string;
  comparedDays: number[];
  throughDay: number;
  daysInMonth: number;
  partial: boolean;
  rows: MonthRankRow[];
  currentYear: number;
  currentRow: MonthRankRow | null;
}

/** Day-of-year for a calendar date. Leap years fall out of the UTC arithmetic. */
export function doyOf(year: number, month: number, day: number): number {
  return Math.floor((Date.UTC(year, month - 1, day) - Date.UTC(year, 0, 1)) / 86400000) + 1;
}

export function daysInMonthOf(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  const suffix = n % 10 === 1 ? "st" : n % 10 === 2 ? "nd" : n % 10 === 3 ? "rd" : "th";
  return `${n}${suffix}`;
}

export interface RankingOptions {
  /** Day-of-month to accumulate up to, inclusive. Defaults to the whole month. */
  throughDay?: number;
  /** Live day-of-month means for the current year, layered over the aggregate. */
  live?: MonthDailyMeans | null;
  minCoverage?: number;
}

/**
 * Rank each year's month-to-date mean: day 1 through `throughDay`, the same
 * window for every year. Days the current year is missing are dropped from all
 * years — comparing a part-month against full ones measures the window, not
 * the weather.
 */
export function computeMonthRanking(
  agg: SensorAggregate,
  month: number,
  currentYear: number,
  options: RankingOptions = {}
): MonthRanking | null {
  const { throughDay, live = null, minCoverage = 0.5 } = options;
  const matrix = agg.yoy_matrix;
  // The current year may be absent from a stale aggregate yet present in the
  // live rollup — that is exactly the case at the start of a new month.
  const currentRowData = matrix[String(currentYear)] ?? {};

  const daysInMonth = daysInMonthOf(currentYear, month);
  const lastDay = Math.min(throughDay ?? daysInMonth, daysInMonth);

  // Live rollup wins over the aggregate: it covers days built after the last
  // pipeline run, and matches the aggregate where both have a day.
  const liveApplies = live != null && live.year === currentYear && live.month === month;
  const currentValue = (d: number): number | null => {
    if (liveApplies) {
      const v = live!.days[String(d)];
      if (v != null && Number.isFinite(v)) return v;
    }
    const fromAgg = currentRowData[String(doyOf(currentYear, month, d))];
    return fromAgg != null && Number.isFinite(fromAgg) ? fromAgg : null;
  };

  const comparedDays: number[] = [];
  for (let d = 1; d <= lastDay; d++) {
    if (currentValue(d) != null) comparedDays.push(d);
  }
  if (comparedDays.length === 0) return null;

  const years = [...new Set([...Object.keys(matrix).map(Number), currentYear])]
    .filter((y) => Number.isFinite(y))
    .sort((a, b) => a - b);

  const scored: Array<Omit<MonthRankRow, "rank">> = [];
  for (const year of years) {
    const row = matrix[String(year)] ?? {};
    const vals: number[] = [];
    for (const d of comparedDays) {
      const v = year === currentYear ? currentValue(d) : row[String(doyOf(year, month, d))];
      if (v != null && Number.isFinite(v)) vals.push(v);
    }
    // A year covering only a handful of the compared days would rank on noise.
    if (vals.length < Math.ceil(comparedDays.length * minCoverage)) continue;
    scored.push({
      year,
      mean: vals.reduce((a, b) => a + b, 0) / vals.length,
      days: vals.length,
      isCurrent: year === currentYear
    });
  }
  if (scored.length === 0) return null;

  const rows: MonthRankRow[] = scored
    .sort((a, b) => b.mean - a.mean)
    .map((r, i) => ({ ...r, rank: i + 1 }));

  const monthLabel = new Intl.DateTimeFormat("en-US", { month: "long" })
    .format(new Date(Date.UTC(2024, month - 1, 15)));

  return {
    month,
    monthLabel,
    comparedDays,
    throughDay: lastDay,
    daysInMonth,
    partial: comparedDays.length < daysInMonth,
    rows,
    currentYear,
    currentRow: rows.find((r) => r.isCurrent) ?? null
  };
}

type SourceMix = { own: number; dwd: number; other: number; dominant: "own" | "dwd" | "sc" | null };

/**
 * Share of each year's readings that came from the rooftop box vs the DWD
 * station, taken from the moment file's per-hour provenance labels.
 */
export function monthSourceMix(month: MomentMonth | null, sensor = "temperatur"): Map<number, SourceMix> {
  const out = new Map<number, SourceMix>();
  if (!month) return out;
  const days = month.sensors[sensor];
  if (!days) return out;

  for (const [i, year] of month.years.entries()) {
    const mix: SourceMix = { own: 0, dwd: 0, other: 0, dominant: null };
    for (const hours of Object.values(days)) {
      for (const cell of Object.values(hours)) {
        const src = cell.src[i];
        if (src == null) continue;
        if (src === "own") mix.own++;
        else if (src.startsWith("dwd")) mix.dwd++;
        else mix.other++;
      }
    }
    const total = mix.own + mix.dwd + mix.other;
    if (total > 0) {
      mix.dominant = mix.own >= mix.dwd && mix.own >= mix.other
        ? "own"
        : mix.dwd >= mix.other ? "dwd" : "sc";
      out.set(year, mix);
    }
  }
  return out;
}

const SRC_COLOR: Record<string, string> = { own: "#0d9488", dwd: "#6b7280", sc: "#f59e0b" };
const SRC_LABEL: Record<string, string> = {
  own: "mostly rooftop box",
  dwd: "mostly DWD München-Stadt (3 km)",
  sc: "mostly citizen sensors"
};

/** Same priority chain as scripts/merge_gapfill.py — see worker/src/sources/gapFill.ts. */
const GAP_FILL_LABELS: Record<string, string> = {
  "dwd-stadt": "DWD München-Stadt",
  "dwd-flughafen": "DWD München-Flughafen",
  "sc-7890": "a nearby citizen sensor",
  "sc-4453": "a nearby citizen sensor"
};

export function buildMonthRanking(
  agg: SensorAggregate | null,
  momentMonth: MomentMonth | null,
  now: Date,
  live: MonthDailyMeans | null = null
): HTMLElement {
  const container = el("section", { className: "month-rank" });
  if (!agg) return container;

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Berlin", year: "numeric", month: "numeric", day: "numeric"
  }).formatToParts(now);
  const num = (t: string) => parseInt(parts.find((p) => p.type === t)!.value, 10);
  const year = num("year");
  const month = num("month");
  const today = num("day");

  const ranking = computeMonthRanking(agg, month, year, { throughDay: today, live });
  if (!ranking) return container;

  const { monthLabel, rows, currentRow, comparedDays, throughDay, daysInMonth, partial } = ranking;
  const lastCompared = comparedDays[comparedDays.length - 1]!;

  container.append(el("h4", { text: `📊 ${monthLabel} 1–${throughDay}, every year ranked` }));

  if (currentRow) {
    container.append(el("p", {
      className: "month-rank-summary",
      text: `${monthLabel} ${year} so far averages ${currentRow.mean.toFixed(1)}°C over ${currentRow.days} ` +
            `day${currentRow.days === 1 ? "" : "s"} — the ${ordinal(currentRow.rank)}-warmest of ${rows.length} years on record.`
    }));
  }

  const mix = monthSourceMix(momentMonth);
  const hi = rows[0]!.mean;
  const lo = rows[rows.length - 1]!.mean;
  const spread = hi - lo || 1;

  const list = el("div", { className: "month-rank-rows" });
  for (const r of rows) {
    const row = el("div", { className: r.isCurrent ? "month-rank-row is-current" : "month-rank-row" });
    const m = mix.get(r.year);
    const dotColor = m?.dominant ? SRC_COLOR[m.dominant]! : "#6b7280";
    const dotTitle = m?.dominant ? SRC_LABEL[m.dominant]! : "source unknown";

    const dot = el("span", { className: "month-rank-dot", text: "●", title: dotTitle });
    dot.setAttribute("style", `color: ${dotColor};`);

    const bar = el("span", { className: "month-rank-bar" });
    const pct = 8 + ((r.mean - lo) / spread) * 92;
    bar.setAttribute("style", `width: ${pct.toFixed(1)}%; background-color: ${r.isCurrent ? "#0d9488" : "#cbd5e1"};`);

    row.append(
      el("span", { className: "month-rank-pos", text: `${r.rank}.` }),
      dot,
      el("span", { className: "month-rank-year", text: r.isCurrent ? `${r.year} (now)` : String(r.year) }),
      el("span", { className: "month-rank-track" }, [bar]),
      el("span", { className: "month-rank-val", text: `${r.mean.toFixed(1)}°C` })
    );
    list.append(row);
  }
  container.append(list);

  const notes = el("div", { className: "month-rank-notes" });
  if (partial) {
    const gaps = throughDay - comparedDays.length;
    notes.append(el("p", {
      text: `Month-to-date: ${monthLabel} 1–${throughDay}, ${comparedDays.length} of ${daysInMonth} days` +
            (gaps > 0 ? ` (${gaps} day${gaps === 1 ? "" : "s"} missing, last compared day ${lastCompared})` : "") +
            `. Every year is averaged over exactly these same days, and the window grows each day until the month turns.`
    }));
  }
  const gapFilledDays = live != null
    ? comparedDays.filter((d) => GAP_FILL_LABELS[live.sources[String(d)] as string] != null)
    : [];
  if (gapFilledDays.length > 0) {
    const labels = [...new Set(gapFilledDays.map((d) => GAP_FILL_LABELS[live!.sources[String(d)] as string]))];
    notes.append(el("p", {
      text: `${gapFilledDays.length} day${gapFilledDays.length === 1 ? "" : "s"} this month had no rooftop-box reading ` +
            `at all, so this year's average uses ${labels.join(" / ")} for those days instead of leaving a gap.`
    }));
  }
  notes.append(el("p", {
    text: "Mixed instruments: 2019–2022 comes from the DWD station 3 km away, 2023 onward mostly from the rooftop box, " +
          "which reads roughly 1°C warmer at the same moment. Differences under ~1°C between those eras say more about " +
          "the thermometer than the weather."
  }));
  notes.append(el("p", {
    text: `Daily means from gap-filled hourly data, ${agg.first_day} to ${agg.last_day}.`
  }));
  container.append(notes);

  return container;
}
